import { useRouterState } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { routeRegistry } from '@/config/route-registry';
import './breadcrumb-style.scss';

interface BreadcrumbProps {
  dynamicRouteTexts?: string[]; // For dynamic segments (UUIDs) - replaces the segment value
}

const Breadcrumb = ({ dynamicRouteTexts = [] }: BreadcrumbProps) => {
  const router = useRouterState();
  const currentLocation = router.location.pathname;

  // Dynamic values will be UUIDs
  const isValidDynamic = (str: string): boolean => {
    const uuidPattern =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    return uuidPattern.test(str);
  };

  /**
   * Find route name by matching path pattern
   * Handles dynamic segments (e.g., /route-survey/abc-123 matches /route-survey/:id)
   */
  const getRouteName = (pathname: string): string | null => {
    // Direct match first
    const directMatch = routeRegistry.find((route) => route.path === pathname);
    if (directMatch) {
      return directMatch.name;
    }

    // Try pattern matching for dynamic segments
    for (const route of routeRegistry) {
      const routePattern = route.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${routePattern}$`);
      if (regex.test(pathname)) {
        return route.name;
      }
    }

    return null;
  };

  /**
   * Get parent route path for a given pathname
   * First checks if parentPath is explicitly defined in registry
   * If not, falls back to parsing the route by removing the last segment
   */
  const getParentRoute = (pathname: string): string | null => {
    // First, check if parentPath is explicitly defined in registry
    for (const route of routeRegistry) {
      // Direct match
      if (route.path === pathname) {
        if (route.parentPath) {
          return route.parentPath;
        }
        break; // Found the route but no explicit parentPath, fall through to parsing
      }
      // Pattern match for dynamic segments
      const routePattern = route.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${routePattern}$`);
      if (regex.test(pathname)) {
        if (route.parentPath) {
          return route.parentPath;
        }
        break; // Found the route but no explicit parentPath, fall through to parsing
      }
    }

    // Fallback: Parse the route by removing the last segment
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) {
      return null;
    }

    // Remove the last segment to get parent path
    const parentPath = '/' + segments.slice(0, -1).join('/');

    // If parent path is just "/", return null (we're at root)
    if (parentPath === '/') {
      return null;
    }

    // Verify that the parent path exists in the registry
    const parentRouteName = getRouteName(parentPath);
    if (parentRouteName) {
      return parentPath;
    }

    return null;
  };

  const getBreadcrumbs = (location: string) => {
    const breadcrumbs: Array<{
      pathname: string;
      name: string;
      active: boolean;
    }> = [];
    let dynamicIndex = 0;

    const segments = location.split('/').filter(Boolean);

    // Build breadcrumbs by progressively building paths and looking them up in registry
    segments.forEach((segment, index) => {
      const currentPathname = '/' + segments.slice(0, index + 1).join('/');

      // Check if segment is a dynamic value (UUID)
      if (isValidDynamic(segment)) {
        // Use dynamic text if provided, otherwise skip this segment
        const dynamicText = dynamicRouteTexts?.[dynamicIndex];
        if (dynamicText) {
          breadcrumbs.push({
            pathname: currentPathname,
            name: dynamicText,
            active: index + 1 === segments.length,
          });
        }
        dynamicIndex++;
      } else {
        // Look up route name from registry
        const routeName = getRouteName(currentPathname);
        if (routeName) {
          breadcrumbs.push({
            pathname: currentPathname,
            name: routeName,
            active: index + 1 === segments.length,
          });
        }
      }
    });

    // Traverse the full parent path chain and add all parent breadcrumbs
    let currentPath = location;
    const visitedPaths = new Set<string>();

    while (currentPath) {
      const parentPath = getParentRoute(currentPath);
      if (!parentPath || visitedPaths.has(parentPath)) {
        break;
      }

      visitedPaths.add(parentPath);
      const parentName = getRouteName(parentPath);
      const alreadyInBreadcrumbs = breadcrumbs.some(
        (b) => b.pathname === parentPath,
      );

      if (parentName && !alreadyInBreadcrumbs) {
        breadcrumbs.unshift({
          pathname: parentPath,
          name: parentName,
          active: false,
        });
      }

      currentPath = parentPath;
    }

    // Override the active breadcrumb name with any remaining unused dynamic text
    if (dynamicIndex < dynamicRouteTexts.length) {
      const last = breadcrumbs[breadcrumbs.length - 1];
      if (last?.active) {
        last.name = dynamicRouteTexts[dynamicIndex];
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs(currentLocation);

  return (
    <nav className="breadcrumb">
      {breadcrumbs.map((breadcrumb, index) => (
        <span
          key={index}
          className={
            breadcrumb.active ? 'breadcrumb-item active' : 'breadcrumb-item'
          }
        >
          {index > 0 && <span className="breadcrumb-separator"> &gt; </span>}
          {breadcrumb.active ? (
            breadcrumb.name
          ) : (
            <Link
              to={breadcrumb.pathname as any}
              className="text-decoration-none"
            >
              {breadcrumb.name}
            </Link>
          )}
        </span>
      ))}
    </nav>
  );
};

export { Breadcrumb };
